/**
 * Модалка управления подразделениями сотрудника.
 * Позволяет привязать сотрудника к нескольким КБ и/или отделам.
 * Если сотрудник - главный конструктор, выбор отделов скрывается.
 */
import { useState, useCallback, useMemo } from "react";
import { ModalShell } from "../ModalShell";
import { FormField } from "../FormField";
import { useAsyncSubmit } from "../../hooks/useAsyncSubmit";
import {
  useEmployeeQuery,
  useUpdateEmployeeMutation,
} from "../../api/queries/employees";
import { useDepartmentsQuery } from "../../api/queries/departments";
import { useKbsQuery } from "../../api/queries/kbs";

export const DeptsModal = ({ empId, onClose, toast }) => {
  const employeeQ = useEmployeeQuery(empId);
  const emp = employeeQ.data;

  if (!emp) {
    return (
      <ModalShell
        title="Подразделения"
        onClose={onClose}
        width={560}
        showSave={false}
        footer={
          <div className="modal-foot">
            <div className="spacer" />
            <button type="button" className="btn ghost" onClick={onClose}>
              Закрыть
            </button>
          </div>
        }
      >
        <div className="empty-note p-4">
          {employeeQ.isError
            ? "Не удалось загрузить данные сотрудника"
            : "Загрузка…"}
        </div>
      </ModalShell>
    );
  }

  return (
    <DeptsForm key={emp.id} emp={emp} empId={empId} onClose={onClose} toast={toast} />
  );
};

const DeptsForm = ({ emp, empId, onClose, toast }) => {
  const departmentsQ = useDepartmentsQuery();
  const kbsQ = useKbsQuery();
  const updateMutation = useUpdateEmployeeMutation();
  const departments = departmentsQ.data ?? [];
  const kbs = kbsQ.data ?? [];

  const isKbChief = useMemo(
    () => !!emp && Array.isArray(emp.roles) && emp.roles.includes("kb_chief"),
    [emp],
  );

  const [kbIds, setKbIds] = useState(() => emp.kbIds || []);

  const [selections, setSelections] = useState(() => {
    return emp.departments.map((d) => {
      const isPrimary = !isKbChief && !!d.primary;
      const entry = {
        deptId: d.deptId ?? null,
        primary: isPrimary,
        position: d.position ?? "",
      };
      if (!isPrimary) entry.rate = d.rate ?? 1;
      return entry;
    });
  });

  const toggleKb = (kbId) => {
    setKbIds((prev) =>
      prev.includes(kbId) ? prev.filter((id) => id !== kbId) : [...prev, kbId],
    );
  };

  const toggleDept = (deptId) => {
    setSelections((prev) => {
      const exists = prev.some((x) => x.deptId === deptId);
      if (exists) return prev.filter((x) => x.deptId !== deptId);
      return [...prev, { deptId, primary: false, position: "", rate: 1 }];
    });
  };

  const toggleExternal = () => {
    setSelections((prev) => {
      const exists = prev.some((x) => x.deptId === null);
      if (exists) return prev.filter((x) => x.deptId !== null);
      return [...prev, { deptId: null, primary: false, position: "", rate: 1 }];
    });
  };

  const setPrimary = (deptId) => {
    if (isKbChief) return;
    setSelections((prev) => {
      const cur = prev.find((x) => x.deptId === deptId);
      if (cur?.primary) {
        return prev.map((x) =>
          x.deptId === deptId ? { ...x, primary: false } : x,
        );
      }
      return prev.map((x) => ({ ...x, primary: x.deptId === deptId }));
    });
  };

  const setPosition = (deptId, pos) => {
    setSelections((prev) =>
      prev.map((x) => (x.deptId === deptId ? { ...x, position: pos } : x)),
    );
  };

  const setRate = (deptId, rate) => {
    setSelections((prev) =>
      prev.map((x) => (x.deptId === deptId ? { ...x, rate } : x)),
    );
  };

  const externalEntry = selections.find((x) => x.deptId === null);
  const hasExternal = !!externalEntry;

  const saveAsync = useCallback(async () => {
    const nextDepartments = selections.map(({ deptId, position, rate }) => {
      const isPrimary =
        !isKbChief && !!selections.find((s) => s.deptId === deptId)?.primary;
      const entry = {
        deptId,
        primary: isPrimary,
        position: position?.trim() || "",
      };
      if (!isPrimary) {
        const numRate = Number(rate);
        if (!Number.isFinite(numRate) || numRate < 0.1 || numRate > 1) {
          throw new Error("Ставка должна быть от 0.1 до 1");
        }
        entry.rate = String(numRate);
      } else {
        entry.rate = "1.0";
      }
      return entry;
    });

    await updateMutation.mutateAsync({
      id: empId,
      body: { departments: nextDepartments, kbIds },
    });
    onClose();
  }, [selections, kbIds, isKbChief, updateMutation, empId, onClose]);

  const { submit: save, isSubmitting } = useAsyncSubmit(saveAsync, (error) => {
    toast(error.message || "Ошибка сохранения подразделений", "error");
  });

  return (
    <ModalShell
      title={`Подразделения - ${emp.last} ${emp.first}`}
      onClose={onClose}
      onSave={save}
      width={560}
      saveDisabled={isSubmitting}
    >
      {isKbChief ? (
        <p className="mut sm">
          Сотрудник - главный конструктор. Его основное место работы - КБ.
        </p>
      ) : (
        <p className="mut sm">
          Сотрудник может числиться в нескольких КБ и отделах.
        </p>
      )}

      <div className="roles-list">
        <div className="mut sm mb-2 font-bold mt-2">Конструкторские бюро:</div>
        {kbs.length === 0 && <div className="mut sm mb-2">Нет доступных КБ</div>}
        {kbs.map((kb) => (
          <div key={kb.id} className="mb-2">
            <label className="roles-item roles-item-clean">
              <input
                type="checkbox"
                checked={kbIds.includes(kb.id)}
                onChange={() => toggleKb(kb.id)}
              />
              <span className="flex-1">{kb.name}</span>
            </label>
          </div>
        ))}

        {!isKbChief && (
          <>
            <div className="mut sm mb-2 font-bold mt-4">Отделы:</div>
            {departments.map((d) => {
              const cur = selections.find((x) => x.deptId === d.id);
              const isPrimary = !isKbChief && cur && cur.primary;
              const isExtra = cur && !isPrimary;
              return (
                <div key={d.id} className="mb-2">
                  <label className="roles-item roles-item-clean">
                    <input
                      type="checkbox"
                      checked={!!cur}
                      onChange={() => toggleDept(d.id)}
                    />
                    <span className="flex-1">{d.name}</span>
                    {cur && (
                      <button
                        type="button"
                        className={"btn ghost sm" + (isPrimary ? " prim-btn" : "")}
                        onClick={(e) => {
                          e.preventDefault();
                          setPrimary(d.id);
                        }}
                      >
                        {isPrimary ? "основное ✓" : "сделать основным"}
                      </button>
                    )}
                  </label>
                  {isExtra && (
                    <>
                      <div className="extra-position-field">
                        <label className="lbl lbl--compact">
                          Должность в этом отделе:
                        </label>
                        <input
                          className="inp extra-position-input"
                          value={cur.position || ""}
                          onChange={(e) => setPosition(d.id, e.target.value)}
                          placeholder="Например: Ведущий инженер (совмещение)"
                        />
                      </div>
                      <div className="extra-position-field">
                        <FormField
                          label="Ставка"
                          type="number"
                          min="0.1"
                          max="1"
                          step="0.1"
                          value={cur.rate ?? 1}
                          onChange={(v) => setRate(d.id, v)}
                          inline
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            <div className="mb-2 mt-4">
              <label className="roles-item roles-item-clean">
                <input
                  type="checkbox"
                  checked={hasExternal}
                  onChange={toggleExternal}
                />
                <span className="flex-1">Вне отделов</span>
              </label>
              {hasExternal && (
                <>
                  <div className="extra-position-field">
                    <label className="lbl lbl--compact">Должность:</label>
                    <input
                      className="inp extra-position-input"
                      value={externalEntry.position || ""}
                      onChange={(e) => setPosition(null, e.target.value)}
                      placeholder="Например: Руководитель проектов"
                    />
                  </div>
                  <div className="extra-position-field">
                    <FormField
                      label="Ставка"
                      type="number"
                      min="0.1"
                      max="1"
                      step="0.1"
                      value={externalEntry.rate ?? 1}
                      onChange={(v) => setRate(null, v)}
                      inline
                    />
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
};
